import { Box, Paper } from '@mui/material'
import { useState } from 'react'
import type { ReactNode } from 'react'

import { TabBar, type TabItem } from '@/components/molecules/TabBar'
import { RADIUS } from '@/shared/styles/radii'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'

import { useFinishAuditMutation } from '../../hooks/mutations/useFinishAuditMutation'
import type { UseGuidanceQueryV3Result } from '../../hooks/queries/useGuidanceQueryV3'
import { usePatientAttributesQuery } from '../../hooks/queries/usePatientAttributesQuery'
import { AuditFace } from './AuditFace'
import { GuidanceFace } from './GuidanceFace'
import type { PrepFace } from './parts'

const FACES: TabItem<PrepFace>[] = [
  { key: 'guidance', label: '服薬指導' },
  { key: 'audit', label: '監査' },
]

interface Props {
  event: YakurekiDetail
  live: UseGuidanceQueryV3Result
  // 指導前準備が completed でないときの 1 行 (生成中… / 失敗 / NSIPS 未紐付け).
  // ga のサマリと同じ部品を使うため、 呼び出し側 (GuidancePrepPaneV3) から受け取る.
  pending: ReactNode
  // 見出しの右端 (失敗時の「再取得」)
  headerAction?: ReactNode
}

// 指導前準備のカード (β). 見出しの「サマリ」を「服薬指導｜監査」の切り替えにする.
// 最初に開くのは服薬指導. 監査は選んだときだけ開く (yakureki-back log/262 決定 1).
// ga は GuidancePrepPaneV3 の今のサマリのまま (このカードは β のユーザーにだけ出る).
//
// 「監査を終える」→ POST → 服薬指導の面へ戻る. 申し送りが空でなければ back が
// 確認要点を作り直し、 指導前準備の 2 秒ごとの GET がそれを受け取る.
export const GuidancePrepCard = ({
  event,
  live,
  pending,
  headerAction,
}: Props) => {
  const [face, setFace] = useState<PrepFace>('guidance')
  // 属性は監査の面を初めて開いたときに 1 回だけ取る
  const [hasOpenedAudit, setHasOpenedAudit] = useState(false)
  // null = まだ打っていない (= 前回の申し送りを初期値にする)
  const [draft, setDraft] = useState<string | null>(null)

  const attributes = usePatientAttributesQuery(event.id, hasOpenedAudit)
  const finishAudit = useFinishAuditMutation(event.id)

  const data = live.status === 'completed' ? live.data : null
  // 終えた直後は取り直し (確認要点の作り直し中は data が null) を待たずに、
  // POST の返り値で申し送りを出す.
  const audit = finishAudit.data ?? data?.audit ?? null

  const changeFace = (next: PrepFace) => {
    if (next === 'audit') setHasOpenedAudit(true)
    setFace(next)
  }

  const finish = () => {
    const note = (draft ?? audit?.handoverNote ?? '').trim()
    finishAudit.mutate(note, {
      onSuccess: () => {
        setDraft(null)
        setFace('guidance')
      },
    })
  }

  return (
    // 器とタブ行は録音ブロックと同じ (Paper outlined・角丸 xl・共通の TabBar). yakureki-back log/262 決定 15
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${RADIUS.xl}px`, overflow: 'hidden' }}
    >
      <TabBar
        tabs={FACES}
        active={face}
        onChange={changeFace}
        rightSlot={headerAction}
      />

      <Box sx={{ px: 2, py: 2 }}>
        {face === 'guidance' ? (
          <GuidanceFace
            event={event}
            data={data}
            pending={pending}
            audit={audit}
          />
        ) : (
          <AuditFace
            event={event}
            data={data}
            pending={pending}
            attributes={{
              data: attributes.data,
              isLoading: attributes.isLoading,
              isError: attributes.isError,
            }}
            audit={audit}
            draft={draft ?? audit?.handoverNote ?? ''}
            onDraftChange={setDraft}
            onFinish={finish}
            isFinishing={finishAudit.isPending}
            isFinishError={finishAudit.isError}
          />
        )}
      </Box>
    </Paper>
  )
}
